import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`audit-tabpanel-${index}`}
      aria-labelledby={`audit-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * 🔍 AUDIT TRAIL PAGE
 * Comprehensive audit trail for admin dashboard
 * - Tab 1: User Activity (all actions)
 * - Tab 2: Supervisor Edits (value changes)
 * - Tab 3: Status Transitions (workflow)
 */
export default function AuditTrailPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User Activity State
  const [userActivity, setUserActivity] = useState<any[]>([]);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(25);
  const [activityTotalCount, setActivityTotalCount] = useState(0);
  const [activityFilters, setActivityFilters] = useState({
    userId: '',
    action: '',
    sampleId: '',
    startDate: '',
    endDate: '',
  });

  // Supervisor Edits State
  const [supervisorEdits, setSupervisorEdits] = useState<any[]>([]);
  const [editsPage, setEditsPage] = useState(0);
  const [editsRowsPerPage, setEditsRowsPerPage] = useState(25);
  const [editsTotalCount, setEditsTotalCount] = useState(0);
  const [editsFilters, setEditsFilters] = useState({
    userId: '',
    sampleId: '',
    element: '',
    startDate: '',
    endDate: '',
  });

  // Status Transitions State
  const [statusTransitions, setStatusTransitions] = useState<any[]>([]);
  const [transitionsPage, setTransitionsPage] = useState(0);
  const [transitionsRowsPerPage, setTransitionsRowsPerPage] = useState(25);
  const [transitionsTotalCount, setTransitionsTotalCount] = useState(0);
  const [transitionsFilters, setTransitionsFilters] = useState({
    sampleId: '',
    lotNumber: '',
    fromStatus: '',
    toStatus: '',
    startDate: '',
    endDate: '',
  });

  // Statistics State
  const [statistics, setStatistics] = useState<any>(null);

  // Tab Change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch User Activity
  const fetchUserActivity = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: String(activityPage + 1),
        limit: String(activityRowsPerPage),
        ...(activityFilters.userId && { userId: activityFilters.userId }),
        ...(activityFilters.action && { action: activityFilters.action }),
        ...(activityFilters.startDate && { startDate: activityFilters.startDate }),
        ...(activityFilters.endDate && { endDate: activityFilters.endDate }),
      });

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/audit/user-activity?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch user activity');

      const data = await response.json();
      setUserActivity(data.data || []);
      setActivityTotalCount(data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Supervisor Edits
  const fetchSupervisorEdits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: String(editsPage + 1),
        limit: String(editsRowsPerPage),
        ...(editsFilters.userId && { userId: editsFilters.userId }),
        ...(editsFilters.sampleId && { sampleId: editsFilters.sampleId }),
        ...(editsFilters.element && { element: editsFilters.element }),
        ...(editsFilters.startDate && { startDate: editsFilters.startDate }),
        ...(editsFilters.endDate && { endDate: editsFilters.endDate }),
      });

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/audit/supervisor-edits?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch supervisor edits');

      const data = await response.json();
      setSupervisorEdits(data.data || []);
      setEditsTotalCount(data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Status Transitions
  const fetchStatusTransitions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: String(transitionsPage + 1),
        limit: String(transitionsRowsPerPage),
        ...(transitionsFilters.sampleId && { sampleId: transitionsFilters.sampleId }),
        ...(transitionsFilters.lotNumber && { lotNumber: transitionsFilters.lotNumber }),
        ...(transitionsFilters.fromStatus && { fromStatus: transitionsFilters.fromStatus }),
        ...(transitionsFilters.toStatus && { toStatus: transitionsFilters.toStatus }),
        ...(transitionsFilters.startDate && { startDate: transitionsFilters.startDate }),
        ...(transitionsFilters.endDate && { endDate: transitionsFilters.endDate }),
      });

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/audit/status-transitions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch status transitions');

      const data = await response.json();
      setStatusTransitions(data.data || []);
      setTransitionsTotalCount(data.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Statistics
  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/audit/statistics', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch statistics');

      const data = await response.json();
      setStatistics(data);
    } catch (err: any) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (tabValue === 0) {
      fetchUserActivity();
    } else if (tabValue === 1) {
      fetchSupervisorEdits();
    } else if (tabValue === 2) {
      fetchStatusTransitions();
    }
  }, [tabValue, activityPage, activityRowsPerPage, editsPage, editsRowsPerPage, transitionsPage, transitionsRowsPerPage]);

  // Load statistics on mount
  useEffect(() => {
    fetchStatistics();
  }, []);

  // Action type labels
  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      SAMPLE_RECEPTION: 'Réception',
      MECHANICAL_PREPARATION: 'Préparation Mécanique',
      AU_CHEMICAL_PREPARATION: 'Préparation Chimique AU',
      ICP_CHEMICAL_PREPARATION: 'Préparation Chimique ICP',
      AU_ANALYSIS: 'Analyse AU',
      ICP_ANALYSIS: 'Analyse ICP',
      AU_VALIDATION: 'Validation AU',
      ICP_VALIDATION: 'Validation ICP',
      CERTIFICATE_GENERATION: 'Génération Certificat',
    };
    return labels[action] || action;
  };

  // Action color
  const getActionColor = (action: string) => {
    const colors: Record<string, any> = {
      SAMPLE_RECEPTION: 'primary',
      MECHANICAL_PREPARATION: 'info',
      AU_CHEMICAL_PREPARATION: 'warning',
      ICP_CHEMICAL_PREPARATION: 'warning',
      AU_ANALYSIS: 'secondary',
      ICP_ANALYSIS: 'secondary',
      AU_VALIDATION: 'success',
      ICP_VALIDATION: 'success',
      CERTIFICATE_GENERATION: 'default',
    };
    return colors[action] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          🔍 Journal d'Audit
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suivi complet des activités, modifications et transitions
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Échantillons
                </Typography>
                <Typography variant="h4">
                  {statistics.summary?.totalSamples || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Modifications Superviseur
                </Typography>
                <Typography variant="h4" sx={{ color: '#ff9800' }}>
                  {statistics.summary?.totalEdits || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Validations
                </Typography>
                <Typography variant="h4" sx={{ color: '#4caf50' }}>
                  {statistics.summary?.totalValidations || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Certificats Générés
                </Typography>
                <Typography variant="h4" sx={{ color: '#2196f3' }}>
                  {statistics.summary?.totalCertificates || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab
            icon={<PersonIcon />}
            label="Activité Utilisateurs"
            iconPosition="start"
          />
          <Tab
            icon={<EditIcon />}
            label="Modifications Superviseur"
            iconPosition="start"
          />
          <Tab
            icon={<SwapVertIcon />}
            label="Transitions de Statut"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab 1: User Activity */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtres
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="ID Utilisateur"
                size="small"
                value={activityFilters.userId}
                onChange={(e) => setActivityFilters({ ...activityFilters, userId: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={activityFilters.action}
                  label="Action"
                  onChange={(e) => setActivityFilters({ ...activityFilters, action: e.target.value })}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="SAMPLE_RECEPTION">Réception</MenuItem>
                  <MenuItem value="MECHANICAL_PREPARATION">Préparation Mécanique</MenuItem>
                  <MenuItem value="AU_ANALYSIS">Analyse AU</MenuItem>
                  <MenuItem value="ICP_ANALYSIS">Analyse ICP</MenuItem>
                  <MenuItem value="AU_VALIDATION">Validation AU</MenuItem>
                  <MenuItem value="ICP_VALIDATION">Validation ICP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Début"
                type="date"
                size="small"
                value={activityFilters.startDate}
                onChange={(e) => setActivityFilters({ ...activityFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Fin"
                type="date"
                size="small"
                value={activityFilters.endDate}
                onChange={(e) => setActivityFilters({ ...activityFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchUserActivity}
                disabled={loading}
              >
                Rechercher
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date/Heure</strong></TableCell>
                <TableCell><strong>Échantillon</strong></TableCell>
                <TableCell><strong>Lot</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Utilisateur</strong></TableCell>
                <TableCell><strong>Détails</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : userActivity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    Aucune activité trouvée
                  </TableCell>
                </TableRow>
              ) : (
                userActivity.map((activity) => (
                  <TableRow key={activity.id} hover>
                    <TableCell>
                      {activity.timestamp ? format(new Date(activity.timestamp), 'dd/MM/yyyy HH:mm') : 'N/A'}
                    </TableCell>
                    <TableCell>{activity.sampleId}</TableCell>
                    <TableCell>
                      <Chip label={activity.lotNumber} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getActionLabel(activity.action)}
                        color={getActionColor(activity.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{activity.userName}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {JSON.stringify(activity.details || {})}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={activityTotalCount}
            page={activityPage}
            onPageChange={(e, newPage) => setActivityPage(newPage)}
            rowsPerPage={activityRowsPerPage}
            onRowsPerPageChange={(e) => {
              setActivityRowsPerPage(parseInt(e.target.value, 10));
              setActivityPage(0);
            }}
            labelRowsPerPage="Lignes par page:"
          />
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Supervisor Edits */}
      <TabPanel value={tabValue} index={1}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtres
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Échantillon"
                size="small"
                value={editsFilters.sampleId}
                onChange={(e) => setEditsFilters({ ...editsFilters, sampleId: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Élément</InputLabel>
                <Select
                  value={editsFilters.element}
                  label="Élément"
                  onChange={(e) => setEditsFilters({ ...editsFilters, element: e.target.value })}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="Ag">Ag</MenuItem>
                  <MenuItem value="Au">Au</MenuItem>
                  <MenuItem value="Cu">Cu</MenuItem>
                  <MenuItem value="Fe">Fe</MenuItem>
                  <MenuItem value="Pb">Pb</MenuItem>
                  <MenuItem value="Zn">Zn</MenuItem>
                  <MenuItem value="Mo">Mo</MenuItem>
                  <MenuItem value="As">As</MenuItem>
                  <MenuItem value="Sn">Sn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Début"
                type="date"
                size="small"
                value={editsFilters.startDate}
                onChange={(e) => setEditsFilters({ ...editsFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Fin"
                type="date"
                size="small"
                value={editsFilters.endDate}
                onChange={(e) => setEditsFilters({ ...editsFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchSupervisorEdits}
                disabled={loading}
              >
                Rechercher
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell><strong>Date/Heure</strong></TableCell>
                <TableCell><strong>Échantillon</strong></TableCell>
                <TableCell><strong>Lot</strong></TableCell>
                <TableCell><strong>Élément</strong></TableCell>
                <TableCell><strong>Valeur Originale</strong></TableCell>
                <TableCell><strong>Valeur Modifiée</strong></TableCell>
                <TableCell><strong>Δ %</strong></TableCell>
                <TableCell><strong>Superviseur</strong></TableCell>
                <TableCell><strong>Raison</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : supervisorEdits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    Aucune modification trouvée
                  </TableCell>
                </TableRow>
              ) : (
                supervisorEdits.map((edit) => (
                  <TableRow key={edit.id} hover>
                    <TableCell>
                      {edit.editedAt ? format(new Date(edit.editedAt), 'dd/MM/yyyy HH:mm') : 'N/A'}
                    </TableCell>
                    <TableCell>{edit.sampleId}</TableCell>
                    <TableCell>
                      <Chip label={edit.lotNumber} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={edit.element} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#d32f2f', textDecoration: 'line-through' }}>
                        {edit.originalValue?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        {edit.editedValue?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${edit.changePercentage?.toFixed(1)}%`}
                        size="small"
                        color={Math.abs(edit.changePercentage) > 10 ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{edit.editedByName}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ maxWidth: 200, display: 'block' }}>
                        {edit.editReason || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={editsTotalCount}
            page={editsPage}
            onPageChange={(e, newPage) => setEditsPage(newPage)}
            rowsPerPage={editsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setEditsRowsPerPage(parseInt(e.target.value, 10));
              setEditsPage(0);
            }}
            labelRowsPerPage="Lignes par page:"
          />
        </TableContainer>
      </TabPanel>

      {/* Tab 3: Status Transitions */}
      <TabPanel value={tabValue} index={2}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtres
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Échantillon"
                size="small"
                value={transitionsFilters.sampleId}
                onChange={(e) => setTransitionsFilters({ ...transitionsFilters, sampleId: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Numéro Lot"
                size="small"
                value={transitionsFilters.lotNumber}
                onChange={(e) => setTransitionsFilters({ ...transitionsFilters, lotNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Début"
                type="date"
                size="small"
                value={transitionsFilters.startDate}
                onChange={(e) => setTransitionsFilters({ ...transitionsFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Date Fin"
                type="date"
                size="small"
                value={transitionsFilters.endDate}
                onChange={(e) => setTransitionsFilters({ ...transitionsFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchStatusTransitions}
                disabled={loading}
              >
                Rechercher
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell><strong>Date/Heure</strong></TableCell>
                <TableCell><strong>Échantillon</strong></TableCell>
                <TableCell><strong>Lot</strong></TableCell>
                <TableCell><strong>De</strong></TableCell>
                <TableCell><strong>→</strong></TableCell>
                <TableCell><strong>Vers</strong></TableCell>
                <TableCell><strong>Changé Par</strong></TableCell>
                <TableCell><strong>Raison</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : statusTransitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    Aucune transition trouvée
                  </TableCell>
                </TableRow>
              ) : (
                statusTransitions.map((transition) => (
                  <TableRow key={transition.id} hover>
                    <TableCell>
                      {transition.timestamp ? format(new Date(transition.timestamp), 'dd/MM/yyyy HH:mm') : 'N/A'}
                    </TableCell>
                    <TableCell>{transition.sampleId}</TableCell>
                    <TableCell>
                      <Chip label={transition.lotNumber} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={transition.fromStatus} size="small" color="default" />
                    </TableCell>
                    <TableCell>
                      <SwapVertIcon sx={{ fontSize: 16 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={transition.toStatus} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{transition.changedByName}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {transition.reason || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={transitionsTotalCount}
            page={transitionsPage}
            onPageChange={(e, newPage) => setTransitionsPage(newPage)}
            rowsPerPage={transitionsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setTransitionsRowsPerPage(parseInt(e.target.value, 10));
              setTransitionsPage(0);
            }}
            labelRowsPerPage="Lignes par page:"
          />
        </TableContainer>
      </TabPanel>
    </Box>
  );
}

